/**
 * ForgePanel.jsx - Forging, Enhancement, Cubing & Clean Slate Panel
 *
 * Four-tab interface:
 * - FORGE: Apply scrolls to enhance equipment (gemstone scrolls)
 * - ENHANCE: Star Force enhancement (star essence)
 * - CUBE: Reroll potential lines (boss cube drops)
 * - CLEAN SLATE: Reset equipment forging
 *
 * CSS Prefix: fp- (Forge Panel)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useGame } from '../../context/GameContext';
import { GEMSTONES, SCROLL_TIERS, FORGING_CONFIG } from '../../data/forging';
import { getRemainingSlots } from '../../modules/forging';
import { STAR_FORCE_CONFIG } from '../../modules/starForce';
import { CUBE_TYPES, CUBING_CONFIG, getGradeInfo } from '../../data/cubing';
import ItemChip from '../UI/ItemChip';
import ForgingModal from '../modals/ForgingModal';
import StarForceModal from '../modals/StarForceModal';
import CubingModal from '../modals/CubingModal';
import './ForgePanel.css';

// Rarity order for sorting (highest to lowest)
const RARITY_ORDER = { mythic: 6, legendary: 5, epic: 4, rare: 3, uncommon: 2, common: 1 };

// Slot type order for sorting
const SLOT_ORDER = { weapon: 1, offhand: 2, helmet: 3, top: 4, bottom: 5, gloves: 6, boots: 7, cape: 8, belt: 9, pendant: 10, ring: 11 };

// Potential grade order for sorting
const GRADE_ORDER = { legendary: 4, unique: 3, epic: 2, rare: 1, null: 0 };

const ForgePanel = () => {
  const { playerCharacter, useCleanSlateScroll, currentTutorial, getCubeCount } = useGame();
  const [activeTab, setActiveTab] = useState('forge'); // 'forge', 'enhance', 'cube', or 'cleanslate'
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedEnhanceItem, setSelectedEnhanceItem] = useState(null);
  const [selectedCubeItem, setSelectedCubeItem] = useState(null);
  const [sortBy, setSortBy] = useState('rarity'); // 'rarity', 'type', 'stars', 'grade'
  const [pendingModal, setPendingModal] = useState(null); // { type: 'forge'|'enhance'|'cube', item }

  // Check if the forge tab tutorial is currently showing
  const isForgeTabTutorialActive = currentTutorial === 'forgeTab';

  // Show pending modal after forge tutorial is dismissed
  useEffect(() => {
    if (pendingModal && !isForgeTabTutorialActive) {
      // Tutorial was dismissed, now show the pending modal
      if (pendingModal.type === 'forge') {
        setSelectedItem(pendingModal.item);
      } else if (pendingModal.type === 'enhance') {
        setSelectedEnhanceItem(pendingModal.item);
      } else if (pendingModal.type === 'cube') {
        setSelectedCubeItem(pendingModal.item);
      }
      setPendingModal(null);
    }
  }, [isForgeTabTutorialActive, pendingModal]);

  // Handle item click - queue modal if forge tutorial is showing
  const handleForgeItemClick = (item) => {
    if (isForgeTabTutorialActive) {
      setPendingModal({ type: 'forge', item });
    } else {
      setSelectedItem(item);
    }
  };

  const handleEnhanceItemClick = (item) => {
    if (isForgeTabTutorialActive) {
      setPendingModal({ type: 'enhance', item });
    } else {
      setSelectedEnhanceItem(item);
    }
  };

  const handleCubeItemClick = (item) => {
    if (isForgeTabTutorialActive) {
      setPendingModal({ type: 'cube', item });
    } else {
      setSelectedCubeItem(item);
    }
  };

  // Get star essence count
  const starEssence = playerCharacter?.starEssence || 0;

  // Count gemstones from inventory
  const gemstones = useMemo(() => {
    const counts = { ruby: 0, sapphire: 0, emerald: 0, topaz: 0, amethyst: 0 };
    if (playerCharacter?.inventory) {
      playerCharacter.inventory.forEach(item => {
        if (item?.isGemstone && counts.hasOwnProperty(item.id)) {
          counts[item.id] += item.quantity || 1;
        }
      });
    }
    return counts;
  }, [playerCharacter?.inventory]);

  // Count cubes from inventory
  const cubeCounts = useMemo(() => {
    const counts = { occultCube: 0, masterCube: 0, meisterCube: 0, redCube: 0, blackCube: 0 };
    if (playerCharacter?.inventory) {
      playerCharacter.inventory.forEach(item => {
        if (item?.isCube && counts.hasOwnProperty(item.id)) {
          counts[item.id] += item.quantity || 1;
        }
      });
    }
    return counts;
  }, [playerCharacter?.inventory]);

  // Total cubes for badge display
  const totalCubes = useMemo(() => {
    return Object.values(cubeCounts).reduce((sum, count) => sum + count, 0);
  }, [cubeCounts]);

  // Get all forgeable equipment (equipped + inventory)
  const forgeableItems = useMemo(() => {
    const items = [];
    if (playerCharacter?.equipment) {
      Object.entries(playerCharacter.equipment).forEach(([slot, item]) => {
        if (item && item.maxGemSlots !== undefined) {
          items.push({ ...item, location: 'equipped', slotName: slot });
        }
      });
    }
    if (playerCharacter?.inventory) {
      playerCharacter.inventory.forEach((item, index) => {
        if (item && item.slot && item.maxGemSlots !== undefined) {
          items.push({ ...item, location: 'inventory', inventoryIndex: index });
        }
      });
    }
    return items;
  }, [playerCharacter?.equipment, playerCharacter?.inventory]);

  // Separate items by forging status
  const { canForgeItems, fullItems, cleanSlateItems } = useMemo(() => {
    const canForge = [];
    const full = [];
    const cleanSlate = [];

    forgeableItems.forEach(item => {
      const remaining = getRemainingSlots(item);
      const hasForging = (item.appliedScrolls?.length || 0) > 0 || (item.failedSlots || 0) > 0;

      if (remaining > 0) {
        canForge.push(item);
      } else {
        full.push(item);
      }

      if (hasForging) {
        cleanSlate.push(item);
      }
    });

    return { canForgeItems: canForge, fullItems: full, cleanSlateItems: cleanSlate };
  }, [forgeableItems]);

  // Separate items by enhance (star force) status
  const { canEnhanceItems, maxStarItems } = useMemo(() => {
    const canEnhance = [];
    const maxStar = [];

    forgeableItems.forEach(item => {
      const currentStars = item.starForce || 0;
      const maxStars = STAR_FORCE_CONFIG.maxStars || 15;

      if (currentStars < maxStars) {
        canEnhance.push(item);
      } else {
        maxStar.push(item);
      }
    });

    return { canEnhanceItems: canEnhance, maxStarItems: maxStar };
  }, [forgeableItems]);

  // Separate items by potential (cubing) status
  const { canCubeItems, legendaryItems } = useMemo(() => {
    const canCube = [];
    const legendary = [];

    forgeableItems.forEach(item => {
      const grade = item.potentialGrade || null;

      if (grade === 'legendary') {
        legendary.push(item);
      } else {
        canCube.push(item);
      }
    });

    return { canCubeItems: canCube, legendaryItems: legendary };
  }, [forgeableItems]);

  // Count Clean Slate Scrolls
  const cleanSlateScrollCount = useMemo(() => {
    if (!playerCharacter?.inventory) return 0;
    const scroll = playerCharacter.inventory.find(i => i.id === 'cleanSlateScroll');
    return scroll ? (scroll.quantity || 1) : 0;
  }, [playerCharacter?.inventory]);

  // Handle clean slate action
  const handleCleanSlate = (item) => {
    if (cleanSlateScrollCount <= 0) return;
    const result = useCleanSlateScroll(item.instanceId);
    if (result.success) {
      console.log(`✨ ${result.message}`);
    } else {
      console.error(result.error);
    }
  };

  const gemstoneOrder = ['ruby', 'sapphire', 'emerald', 'topaz', 'amethyst'];

  // Sort items based on current sort option
  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      if (sortBy === 'rarity') {
        // Sort by rarity (highest first), then by slot type
        const rarityDiff = (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0);
        if (rarityDiff !== 0) return rarityDiff;
        return (SLOT_ORDER[a.slot] || 99) - (SLOT_ORDER[b.slot] || 99);
      } else if (sortBy === 'type') {
        // Sort by slot type, then by rarity (highest first)
        const typeDiff = (SLOT_ORDER[a.slot] || 99) - (SLOT_ORDER[b.slot] || 99);
        if (typeDiff !== 0) return typeDiff;
        return (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0);
      } else if (sortBy === 'stars') {
        // Sort by star force (highest first), then by rarity
        const starsDiff = (b.starForce || 0) - (a.starForce || 0);
        if (starsDiff !== 0) return starsDiff;
        return (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0);
      } else if (sortBy === 'grade') {
        // Sort by potential grade (highest first), then by rarity
        const gradeDiff = (GRADE_ORDER[b.potentialGrade] || 0) - (GRADE_ORDER[a.potentialGrade] || 0);
        if (gradeDiff !== 0) return gradeDiff;
        return (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0);
      }
      return 0;
    });
  };

  // Sorted item lists
  const sortedForgeItems = useMemo(() => sortItems(canForgeItems), [canForgeItems, sortBy]);
  const sortedEnhanceItems = useMemo(() => sortItems(canEnhanceItems), [canEnhanceItems, sortBy]);
  const sortedCleanSlateItems = useMemo(() => sortItems(cleanSlateItems), [cleanSlateItems, sortBy]);
  const sortedCubeItems = useMemo(() => sortItems(canCubeItems), [canCubeItems, sortBy]);

  return (
    <div className="fp-panel">
      {/* Header with Title */}
      <div className="fp-header">
        <div className="fp-title">
          <span className="fp-title-icon">⚒️</span>
          <span className="fp-title-text">Forge & Enhancement</span>
        </div>
      </div>

      {/* Tab Header */}
      <div className="fp-tabs">
        <button
          className={`fp-tab ${activeTab === 'forge' ? 'active' : ''}`}
          onClick={() => setActiveTab('forge')}
        >
          <span className="fp-tab-icon">💎</span>
          <span className="fp-tab-label">Socket</span>
          <span className="fp-tab-badge">{canForgeItems.length}</span>
        </button>
        <button
          className={`fp-tab fp-enhance-tab ${activeTab === 'enhance' ? 'active' : ''}`}
          onClick={() => setActiveTab('enhance')}
        >
          <span className="fp-tab-icon">⭐</span>
          <span className="fp-tab-label">Enhance</span>
          <span className="fp-tab-badge">{canEnhanceItems.length}</span>
        </button>
        <button
          className={`fp-tab fp-cube-tab ${activeTab === 'cube' ? 'active' : ''}`}
          onClick={() => setActiveTab('cube')}
        >
          <span className="fp-tab-icon">🎲</span>
          <span className="fp-tab-label">Cube</span>
          <span className="fp-tab-badge">{totalCubes}</span>
        </button>
        <button
          className={`fp-tab fp-cleanslate-tab ${activeTab === 'cleanslate' ? 'active' : ''}`}
          onClick={() => setActiveTab('cleanslate')}
        >
          <span className="fp-tab-icon">🔄</span>
          <span className="fp-tab-label">Clean Slate</span>
          <span className="fp-tab-badge">{cleanSlateScrollCount}📜</span>
        </button>
      </div>

      {/* FORGE TAB */}
      {activeTab === 'forge' && (
        <div className="fp-content">
          {/* Two-column layout */}
          <div className="fp-columns">
            {/* LEFT COLUMN: Equipment + Tips */}
            <div className="fp-left-column">
              {/* Equipment Grid */}
              <div className="fp-equipment-area">
                <div className="fp-section-header">
                  <span>Select Equipment</span>
                  <div className="fp-sort-controls">
                    <button
                      className={`fp-sort-btn ${sortBy === 'rarity' ? 'active' : ''}`}
                      onClick={() => setSortBy('rarity')}
                      title="Sort by Rarity"
                    >Rarity</button>
                    <button
                      className={`fp-sort-btn ${sortBy === 'type' ? 'active' : ''}`}
                      onClick={() => setSortBy('type')}
                      title="Sort by Type"
                    >Type</button>
                  </div>
                </div>

                {forgeableItems.length === 0 ? (
                  <div className="fp-empty">
                    <span className="fp-empty-icon">⚔️</span>
                    <span className="fp-empty-text">No equipment available</span>
                    <span className="fp-empty-hint">Obtain equipment from dungeons!</span>
                  </div>
                ) : (
                  <div className="fp-items-scroll">
                    {/* Available items */}
                    {sortedForgeItems.length > 0 && (
                      <div className="fp-items-grid">
                        {sortedForgeItems.map(item => {
                          const remaining = getRemainingSlots(item);
                          const used = item.appliedScrolls?.length || 0;
                          const failed = item.failedSlots || 0;

                          return (
                            <div
                              key={item.instanceId}
                              className={`fp-item-card ${item.location === 'equipped' ? 'fp-equipped' : ''}`}
                              onClick={() => handleForgeItemClick(item)}
                            >
                              <div className="fp-item-chip-wrap">
                                <ItemChip item={item} size="medium" showName={false} />
                                {item.location === 'equipped' && (
                                  <span className="fp-equipped-badge" title="Currently Equipped">E</span>
                                )}
                              </div>
                              <div className="fp-item-details">
                                <div className="fp-item-name">{item.name}</div>
                                <div className="fp-item-slots">
                                  <span className="fp-used">{used}</span>
                                  {failed > 0 && <span className="fp-failed">+{failed}✗</span>}
                                  <span className="fp-sep">/</span>
                                  <span className="fp-max">{item.maxGemSlots}</span>
                                </div>
                              </div>
                              <div className="fp-item-remaining">{remaining} slots</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Fully scrolled */}
                    {fullItems.length > 0 && (
                      <div className="fp-full-section">
                        <div className="fp-full-header">Fully Scrolled ({fullItems.length})</div>
                        <div className="fp-full-items">
                          {fullItems.map(item => (
                            <div key={item.instanceId} className={`fp-full-item ${item.location === 'equipped' ? 'fp-equipped' : ''}`} title={`${item.name}${item.location === 'equipped' ? ' (Equipped)' : ''}`}>
                              <ItemChip item={item} size="small" showName={false} />
                              {item.location === 'equipped' && (
                                <span className="fp-equipped-badge-small" title="Currently Equipped">E</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tip */}
              <div className="fp-tip">
                <span className="fp-tip-icon">💡</span>
                Failed scrolls permanently destroy a slot. Use lower tiers for safer upgrades!
              </div>
            </div>

            {/* RIGHT COLUMN: Gemstones + Tiers */}
            <div className="fp-right-column">
              {/* Gemstones Section */}
              <div className="fp-gems-section">
                <div className="fp-section-header">
                  <span>Gemstones</span>
                </div>
                <div className="fp-gems-list">
                  {gemstoneOrder.map(gemId => {
                    const gem = GEMSTONES[gemId];
                    const count = gemstones[gemId] || 0;
                    return (
                      <div
                        key={gemId}
                        className={`fp-gem-badge ${count === 0 ? 'empty' : ''}`}
                        style={{ '--gem-color': gem.color }}
                        title={gem.name}
                      >
                        <span className="fp-gem-icon">{gem.icon}</span>
                        <span className="fp-gem-count">{count}</span>
                        <span className="fp-gem-name">{gem.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tier Rates Section */}
              <div className="fp-tiers-section">
                <div className="fp-section-header">
                  <span>Gem Tiers</span>
                </div>
                <div className="fp-tiers-list">
                  {SCROLL_TIERS.map(tier => {
                    const rate = Math.round(FORGING_CONFIG.successRates[tier] * 100);
                    return (
                      <div key={tier} className="fp-tier-badge" title={`Tier ${tier}: ${rate}% success`}>
                        <span className="fp-tier-num">T{tier}</span>
                        <span className="fp-tier-rate" style={{
                          color: rate >= 70 ? '#80b858' : rate >= 50 ? '#c8c050' : rate >= 30 ? '#c8a050' : '#c06060'
                        }}>{rate}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ENHANCE TAB (Star Force) */}
      {activeTab === 'enhance' && (
        <div className="fp-content">
          {/* Two-column layout */}
          <div className="fp-columns">
            {/* LEFT COLUMN: Equipment + Tips */}
            <div className="fp-left-column">
              {/* Equipment Grid */}
              <div className="fp-equipment-area">
                <div className="fp-section-header">
                  <span>Select Equipment</span>
                  <div className="fp-sort-controls">
                    <button
                      className={`fp-sort-btn ${sortBy === 'rarity' ? 'active' : ''}`}
                      onClick={() => setSortBy('rarity')}
                      title="Sort by Rarity"
                    >Rarity</button>
                    <button
                      className={`fp-sort-btn ${sortBy === 'type' ? 'active' : ''}`}
                      onClick={() => setSortBy('type')}
                      title="Sort by Type"
                    >Type</button>
                    <button
                      className={`fp-sort-btn ${sortBy === 'stars' ? 'active' : ''}`}
                      onClick={() => setSortBy('stars')}
                      title="Sort by Stars"
                    >Stars</button>
                  </div>
                </div>

                {forgeableItems.length === 0 ? (
                  <div className="fp-empty">
                    <span className="fp-empty-icon">⚔️</span>
                    <span className="fp-empty-text">No equipment available</span>
                    <span className="fp-empty-hint">Obtain equipment from dungeons!</span>
                  </div>
                ) : (
                  <div className="fp-items-scroll">
                    {/* Available items */}
                    {sortedEnhanceItems.length > 0 && (
                      <div className="fp-items-grid">
                        {sortedEnhanceItems.map(item => {
                          const currentStars = item.starForce || 0;
                          const maxStars = STAR_FORCE_CONFIG.maxStars || 15;

                          return (
                            <div
                              key={item.instanceId}
                              className={`fp-item-card ${item.location === 'equipped' ? 'fp-equipped' : ''}`}
                              onClick={() => handleEnhanceItemClick(item)}
                            >
                              <div className="fp-item-chip-wrap">
                                <ItemChip item={item} size="medium" showName={false} />
                                {item.location === 'equipped' && (
                                  <span className="fp-equipped-badge" title="Currently Equipped">E</span>
                                )}
                              </div>
                              <div className="fp-item-details">
                                <div className="fp-item-name">{item.name}</div>
                                <div className="fp-item-slots">
                                  <span className="fp-star">{currentStars > 0 ? '★' : '☆'}</span>
                                  <span className="fp-used">{currentStars}</span>
                                  <span className="fp-sep">/</span>
                                  <span className="fp-max">{maxStars}</span>
                                </div>
                              </div>
                              <div className="fp-item-remaining">Enhance</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Max starred items */}
                    {maxStarItems.length > 0 && (
                      <div className="fp-full-section">
                        <div className="fp-full-header">Max Stars ({maxStarItems.length})</div>
                        <div className="fp-full-items">
                          {maxStarItems.map(item => (
                            <div key={item.instanceId} className={`fp-full-item fp-max-star ${item.location === 'equipped' ? 'fp-equipped' : ''}`} title={`${item.name} - Max Stars!${item.location === 'equipped' ? ' (Equipped)' : ''}`}>
                              <ItemChip item={item} size="small" showName={false} />
                              {item.location === 'equipped' && (
                                <span className="fp-equipped-badge-small" title="Currently Equipped">E</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tip */}
              <div className="fp-tip">
                <span className="fp-tip-icon">💡</span>
                Star Force uses Star Essence (✦). Stars 6+ can downgrade on failure!
              </div>
            </div>

            {/* RIGHT COLUMN: Star Essence + Info */}
            <div className="fp-right-column">
              {/* Star Essence Section */}
              <div className="fp-gems-section">
                <div className="fp-section-header">
                  <span>Star Essence</span>
                </div>
                <div className="fp-gems-list">
                  <div className="fp-gem-badge" style={{ '--gem-color': '#facc15' }}>
                    <span className="fp-gem-icon">✦</span>
                    <span className="fp-gem-count">{starEssence.toLocaleString()}</span>
                    <span className="fp-gem-name">Available</span>
                  </div>
                </div>
              </div>

              {/* Star Force Info Section */}
              <div className="fp-tiers-section">
                <div className="fp-section-header">
                  <span>Star Force Info</span>
                </div>
                <div className="fp-tiers-list">
                  <div className="fp-tier-badge">
                    <span className="fp-tier-num">Max</span>
                    <span className="fp-tier-rate" style={{ color: '#facc15' }}>{STAR_FORCE_CONFIG.maxStars}★</span>
                  </div>
                  <div className="fp-tier-badge">
                    <span className="fp-tier-num">Safe</span>
                    <span className="fp-tier-rate" style={{ color: '#80b858' }}>0-5★</span>
                  </div>
                  <div className="fp-tier-badge">
                    <span className="fp-tier-num">Risk</span>
                    <span className="fp-tier-rate" style={{ color: '#f59e0b' }}>6★+</span>
                  </div>
                  <div className="fp-tier-badge">
                    <span className="fp-tier-num">Guard</span>
                    <span className="fp-tier-rate" style={{ color: '#06b6d4' }}>6-10★</span>
                  </div>
                  <div className="fp-tier-badge">
                    <span className="fp-tier-num">Pity</span>
                    <span className="fp-tier-rate" style={{ color: '#a855f7' }}>+10%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CUBE TAB */}
      {activeTab === 'cube' && (
        <div className="fp-content">
          {/* Two-column layout */}
          <div className="fp-columns">
            {/* LEFT COLUMN: Equipment + Tips */}
            <div className="fp-left-column">
              {/* Equipment Grid */}
              <div className="fp-equipment-area">
                <div className="fp-section-header">
                  <span>Select Equipment</span>
                  <div className="fp-sort-controls">
                    <button
                      className={`fp-sort-btn ${sortBy === 'rarity' ? 'active' : ''}`}
                      onClick={() => setSortBy('rarity')}
                      title="Sort by Rarity"
                    >Rarity</button>
                    <button
                      className={`fp-sort-btn ${sortBy === 'type' ? 'active' : ''}`}
                      onClick={() => setSortBy('type')}
                      title="Sort by Type"
                    >Type</button>
                    <button
                      className={`fp-sort-btn ${sortBy === 'grade' ? 'active' : ''}`}
                      onClick={() => setSortBy('grade')}
                      title="Sort by Potential Grade"
                    >Grade</button>
                  </div>
                </div>

                {forgeableItems.length === 0 ? (
                  <div className="fp-empty">
                    <span className="fp-empty-icon">⚔️</span>
                    <span className="fp-empty-text">No equipment available</span>
                    <span className="fp-empty-hint">Obtain equipment from dungeons!</span>
                  </div>
                ) : (
                  <div className="fp-items-scroll">
                    {/* Available items */}
                    {sortedCubeItems.length > 0 && (
                      <div className="fp-items-grid">
                        {sortedCubeItems.map(item => {
                          const grade = item.potentialGrade || null;
                          const gradeInfo = grade ? getGradeInfo(grade) : null;
                          const lines = item.potentialLines?.length || 0;

                          return (
                            <div
                              key={item.instanceId}
                              className={`fp-item-card ${item.location === 'equipped' ? 'fp-equipped' : ''}`}
                              onClick={() => handleCubeItemClick(item)}
                            >
                              <div className="fp-item-chip-wrap">
                                <ItemChip item={item} size="medium" showName={false} />
                                {item.location === 'equipped' && (
                                  <span className="fp-equipped-badge" title="Currently Equipped">E</span>
                                )}
                              </div>
                              <div className="fp-item-details">
                                <div className="fp-item-name">{item.name}</div>
                                <div className="fp-item-slots">
                                  {gradeInfo ? (
                                    <>
                                      <span className="fp-grade-icon" style={{ color: gradeInfo.color }}>{gradeInfo.icon}</span>
                                      <span className="fp-grade-name" style={{ color: gradeInfo.color }}>{gradeInfo.shortCode}</span>
                                      <span className="fp-sep">·</span>
                                      <span className="fp-lines">{lines} lines</span>
                                    </>
                                  ) : (
                                    <span className="fp-no-grade">No Potential</span>
                                  )}
                                </div>
                              </div>
                              <div className="fp-item-remaining">
                                {grade ? 'Reroll' : 'Reveal'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Legendary items (max grade) */}
                    {legendaryItems.length > 0 && (
                      <div className="fp-full-section">
                        <div className="fp-full-header">Legendary Potential ({legendaryItems.length})</div>
                        <div className="fp-full-items">
                          {legendaryItems.map(item => (
                            <div
                              key={item.instanceId}
                              className={`fp-full-item fp-legendary-potential ${item.location === 'equipped' ? 'fp-equipped' : ''}`}
                              title={`${item.name} - Legendary Potential${item.location === 'equipped' ? ' (Equipped)' : ''}`}
                              onClick={() => handleCubeItemClick(item)}
                            >
                              <ItemChip item={item} size="small" showName={false} />
                              {item.location === 'equipped' && (
                                <span className="fp-equipped-badge-small" title="Currently Equipped">E</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tip */}
              <div className="fp-tip">
                <span className="fp-tip-icon">💡</span>
                Cubes reroll potential lines. Higher cubes have better grade-up chances!
              </div>
            </div>

            {/* RIGHT COLUMN: Cubes + Grade Info */}
            <div className="fp-right-column">
              {/* Cubes Section */}
              <div className="fp-gems-section">
                <div className="fp-section-header">
                  <span>Cubes</span>
                </div>
                <div className="fp-gems-list">
                  {Object.entries(CUBE_TYPES).map(([cubeId, cube]) => {
                    const count = cubeCounts[cubeId] || 0;
                    return (
                      <div
                        key={cubeId}
                        className={`fp-gem-badge ${count === 0 ? 'empty' : ''}`}
                        style={{ '--gem-color': cube.rarity === 'legendary' ? '#22c55e' : cube.rarity === 'epic' ? '#8b5cf6' : cube.rarity === 'rare' ? '#3b82f6' : '#9ca3af' }}
                        title={cube.description}
                      >
                        <span className="fp-gem-icon">{cube.icon}</span>
                        <span className="fp-gem-count">{count}</span>
                        <span className="fp-gem-name">{cube.name.replace(' Cube', '')}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Grade Info Section */}
              <div className="fp-tiers-section">
                <div className="fp-section-header">
                  <span>Potential Grades</span>
                </div>
                <div className="fp-tiers-list">
                  {Object.entries(CUBING_CONFIG.gradeInfo).map(([grade, info]) => (
                    <div key={grade} className="fp-tier-badge" title={`${info.name} - ${CUBING_CONFIG.linesPerGrade[grade]} lines`}>
                      <span className="fp-tier-num">{info.icon}</span>
                      <span className="fp-tier-rate" style={{ color: info.color }}>{info.shortCode}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CLEAN SLATE TAB */}
      {activeTab === 'cleanslate' && (
        <div className="fp-cs-content">
          {/* Header with scroll count */}
          <div className="fp-cs-header">
            <div className="fp-cs-scroll-display">
              <span className="fp-cs-scroll-icon">📜</span>
              <span className="fp-cs-scroll-count">{cleanSlateScrollCount}</span>
              <span className="fp-cs-scroll-label">Clean Slate Scrolls</span>
            </div>
            <div className="fp-cs-desc">
              Reset equipment to remove all applied scrolls and restore failed slots
            </div>
          </div>

          {/* Equipment to reset */}
          <div className="fp-cs-equipment-area">
            <div className="fp-cs-section-header">
              <span>Select Equipment to Reset</span>
              <span className="fp-cs-section-count">{cleanSlateItems.length} with forging</span>
            </div>

            {cleanSlateScrollCount === 0 ? (
              <div className="fp-cs-empty">
                <span className="fp-cs-empty-icon">📜</span>
                <span className="fp-cs-empty-text">No Clean Slate Scrolls</span>
                <span className="fp-cs-empty-hint">Drops from dungeon bosses (Tier 2+)</span>
              </div>
            ) : cleanSlateItems.length === 0 ? (
              <div className="fp-cs-empty">
                <span className="fp-cs-empty-icon">✨</span>
                <span className="fp-cs-empty-text">No forged equipment</span>
                <span className="fp-cs-empty-hint">Apply scrolls to equipment first</span>
              </div>
            ) : (
              <div className="fp-cs-items-scroll">
                <div className="fp-cs-items-grid">
                  {sortedCleanSlateItems.map(item => {
                    const scrollsApplied = item.appliedScrolls?.length || 0;
                    const failedSlots = item.failedSlots || 0;
                    const maxSlots = item.maxGemSlots || 7;

                    return (
                      <div
                        key={item.instanceId}
                        className={`fp-cs-item-card ${item.location === 'equipped' ? 'fp-equipped' : ''}`}
                        onClick={() => handleCleanSlate(item)}
                      >
                        <div className="fp-cs-item-chip-wrap">
                          <ItemChip item={item} size="medium" showName={false} />
                          {item.location === 'equipped' && (
                            <span className="fp-equipped-badge" title="Currently Equipped">E</span>
                          )}
                        </div>
                        <div className="fp-cs-item-details">
                          <div className="fp-cs-item-name">{item.name}</div>
                          <div className="fp-cs-forging-info">
                            {scrollsApplied > 0 && (
                              <span className="fp-cs-info-scrolls">+{scrollsApplied} scrolls</span>
                            )}
                            {failedSlots > 0 && (
                              <span className="fp-cs-info-failed">{failedSlots} failed</span>
                            )}
                          </div>
                          <div className="fp-cs-slots-bar">
                            {Array.from({ length: maxSlots }).map((_, i) => {
                              let slotClass = 'fp-cs-slot empty';
                              if (i < scrollsApplied) slotClass = 'fp-cs-slot filled';
                              else if (i < scrollsApplied + failedSlots) slotClass = 'fp-cs-slot failed';
                              return <div key={i} className={slotClass} />;
                            })}
                          </div>
                        </div>
                        <button className="fp-cs-reset-btn">
                          <span>Reset</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Warning */}
          <div className="fp-cs-warning">
            <span className="fp-cs-warning-icon">⚠️</span>
            This will remove ALL scroll bonuses from the item. Use carefully!
          </div>
        </div>
      )}

      {/* Forging Modal */}
      {selectedItem && (
        <ForgingModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* Star Force Modal */}
      {selectedEnhanceItem && (
        <StarForceModal
          item={selectedEnhanceItem}
          onClose={() => setSelectedEnhanceItem(null)}
        />
      )}

      {/* Cubing Modal */}
      {selectedCubeItem && (
        <CubingModal
          item={selectedCubeItem}
          onClose={() => setSelectedCubeItem(null)}
        />
      )}
    </div>
  );
};

export default ForgePanel;
